import Script from "next/script";
import { useEffect } from "react";
import { useAppSelector } from "@/store/hooks";
import { initAutoClickTracking } from "@/utils/tracking";

// 集成应用的 appKey。改从环境变量取，不再硬编码在源码里（安全评审 CW-05）。
// 说明：这是前端埋点 SDK 的应用标识，按 QuickTracking 的设计本就要出现在页面上，
// 因此挪到 NEXT_PUBLIC_ 变量只是去掉源码硬编码，并不能让它不公开。
// CI 未注入时回落到原值，保证埋点不断。
const APP_KEY = process.env.NEXT_PUBLIC_QT_APP_KEY || "hoz1gn4n4z5jwdocnbw8c8tc";

// 日志服务端域名：国内站 / 国际站使用不同的阿里云区域域名
const TRACK_DOMAIN =
  process.env.SITE === "cn"
    ? "https://quicktracking-qlc-ap-southeast-1.aliyuncs.com"
    : "https://quicktracking-qlc-ap-southeast.alibabacloud.com";

// 站点标识：cn / gl
const SITE = process.env.SITE || "cn";

// 部署环境：dev/stg/uat/uatPre/prd，本地 next dev 未注入时回落到 local。
// 作为全局属性附加到所有上报，用于在 QuickTracking 控制台区分数据来源。
const APP_ENV = process.env.APP_ENV || "local";

/**
 * 阿里云 QuickTracking Web SDK 接入。
 *
 * - SDK 引入与初始化保持顺序，确保 aplus_queue 不被污染。
 * - 本项目为 Next.js 单页应用，路由切换走 history.pushState，
 *   通过 $ACE_TRACKER_CONFIG.enableHistory 让 SDK 自动监听路由变化补报 PV，
 *   否则只能采集到首屏 PV。
 * - 登录态下用 QuickTracking 用户标识 _user_id 上报 uuid，未登录时置空。
 * - SDK 只自动采 PV，点击不采。这里挂一个全局点击代理兜底所有按钮/链接，
 *   关键按钮再用 data-qt / trackClick 补语义名与业务参数（见 utils/tracking.ts）。
 */
const QuickTracking = () => {
  // 登录态下的用户 uuid（未登录为 undefined）
  const uuid = useAppSelector((state) => state.user.userInfo?.uuid);

  // 全局点击代理，只在客户端挂一次，随应用卸载解绑
  useEffect(() => initAutoClickTracking(), []);

  // 跟随登录态设置 _user_id：登录（有 uuid）时上报，登出/未登录时置空，避免错误归因
  useEffect(() => {
    if (typeof window === "undefined") return;
    const queue = ((window as any).aplus_queue = (window as any).aplus_queue || []);
    queue.push({
      action: "aplus.setMetaInfo",
      arguments: ["_user_id", uuid || ""],
    });
  }, [uuid]);

  return (
    <>
      {/* 引入 QuickTracking SDK，并开启单页应用 history 路由自动采集 */}
      <Script
        id="beacon-aplus-loader"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `window.$ACE_TRACKER_CONFIG = window.$ACE_TRACKER_CONFIG || { enableHistory: true, enableHash: false };
(function(w, d, s, q) { w[q] = w[q] || []; var f=d.getElementsByTagName(s)[0],j=d.createElement(s); j.async=true; j.id='beacon-aplus'; j.src='https://o.alicdn.com/QTSDK/quicktracking-sdk/qt_web.cjs.js'; f.parentNode.insertBefore(j, f);})(window, document, 'script', 'aplus_queue');`,
        }}
      />
      {/* 初始化 appKey 与日志域名，紧跟 SDK 引入之后执行 */}
      <Script
        id="beacon-aplus-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `window.aplus_queue = window.aplus_queue || [];
aplus_queue.push({ action: 'aplus.setMetaInfo', arguments: ['appKey', '${APP_KEY}'] });
aplus_queue.push({ action: 'aplus.setMetaInfo', arguments: ['trackDomain', '${TRACK_DOMAIN}'] });
aplus_queue.push({ action: 'aplus.setMetaInfo', arguments: ['globalproperty', { env: '${APP_ENV}', site: '${SITE}' }] });`,
        }}
      />
    </>
  );
};

export default QuickTracking;
